import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Phone, MessageCircle, Mail, Globe, UserPlus, Share2, Loader2, ArrowUpRight, CalendarClock,
} from "lucide-react";
import api from "@/lib/api";
import { downloadVCard, shareCard } from "@/lib/vcard";
import { socialIcon } from "@/pages/CardEditor";
import CardPreview from "@/components/xuma/CardPreview";
import ContactForm from "@/components/xuma/ContactForm";
import ChatWidget from "@/components/xuma/ChatWidget";
import BookingDialog from "@/components/xuma/BookingDialog";
import { Button } from "@/components/ui/button";

export default function PublicCard() {
  const { slug } = useParams();
  const [card, setCard] = useState(undefined);
  const [booking, setBooking] = useState(false);

  useEffect(() => {
    api.get(`/public/card/${slug}`)
      .then(({ data }) => setCard(data))
      .catch(() => setCard(null));
  }, [slug]);

  if (card === undefined) {
    return <div className="min-h-screen bg-xuma-bg grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-xuma-primary" /></div>;
  }

  if (card === null) {
    return (
      <div className="min-h-screen bg-xuma-bg grid place-items-center px-6 text-center">
        <div>
          <h1 className="font-heading font-extrabold text-3xl text-xuma-dark">Tarjeta no encontrada</h1>
          <p className="mt-3 text-xuma-muted">Esta tarjeta no existe o aún no está publicada.</p>
          <Link to="/" className="inline-block mt-6"><Button className="rounded-full bg-xuma-primary hover:bg-xuma-primaryHover text-white">Crear la mía</Button></Link>
        </div>
      </div>
    );
  }

  const primary = card.colors?.primary || "#EF5A2A";
  const publicUrl = `${window.location.origin}/c/${slug}`;
  const shareUrl = `${process.env.REACT_APP_BACKEND_URL}/api/public/card/${slug}/share`;
  const links = card.links || [];
  const photos = card.photos || [];
  const socials = (card.socials || []).filter((s) => s.url);

  const track = (type) => { api.post(`/public/card/${slug}/event`, { type }).catch(() => {}); };

  const onShare = async () => {
    track("share");
    const res = await shareCard(shareUrl, card.name);
    if (res === "copied") toast.success("Enlace copiado");
    else if (res === "error") toast.error("No se pudo compartir");
  };

  const onSaveVcard = () => { track("save"); downloadVCard(card); };

  const waLink = card.whatsapp
    ? `https://wa.me/${card.whatsapp.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Hola ${card.name}, vi tu tarjeta digital y me gustaría más información.`)}`
    : null;

  return (
    <div className="min-h-screen bg-xuma-bg">
      <div className="max-w-lg mx-auto px-5 py-10">
        <CardPreview card={card} publicUrl={publicUrl} />

        {/* Main CTAs */}
        <div className="mt-6 grid grid-cols-2 gap-3">
          <Button data-testid="save-vcard-btn" onClick={onSaveVcard}
            className="h-12 rounded-2xl text-white gap-2" style={{ background: primary }}>
            <UserPlus className="h-4 w-4" /> Guardar contacto
          </Button>
          <Button data-testid="share-btn" onClick={onShare} variant="outline"
            className="h-12 rounded-2xl border-xuma-border gap-2">
            <Share2 className="h-4 w-4" /> Compartir
          </Button>
        </div>

        {card.agenda?.enabled && (
          <Button data-testid="open-booking-btn" onClick={() => setBooking(true)}
            className="w-full mt-3 h-12 rounded-2xl text-white gap-2" style={{ background: primary }}>
            <CalendarClock className="h-4 w-4" /> Agendar una cita
          </Button>
        )}

        {/* Quick actions */}
        <div className="mt-3 space-y-3">
          {waLink && <ActionRow href={waLink} onClick={() => track("whatsapp")} icon={MessageCircle} color="#25D366" label="Envíame un WhatsApp" testid="action-whatsapp" external />}
          {card.phone && <ActionRow href={`tel:${card.phone}`} onClick={() => track("phone")} icon={Phone} color={primary} label="Llamar por teléfono" testid="action-phone" />}
          {card.email && <ActionRow href={`mailto:${card.email}`} onClick={() => track("email")} icon={Mail} color="#2563EB" label="Envíame un correo" testid="action-email" />}
          {card.website && <ActionRow href={card.website} onClick={() => track("website")} external icon={Globe} color="#1C1917" label="Visita mi sitio web" testid="action-website" />}
        </div>

        {/* Brief */}
        {card.brief && (
          <div className="mt-8 rounded-2xl bg-white border border-xuma-border p-6">
            <p className="text-xs uppercase tracking-[0.2em] text-xuma-muted mb-2">Sobre mí</p>
            <p className="text-xuma-text leading-relaxed whitespace-pre-line">{card.brief}</p>
          </div>
        )}

        {/* Gallery */}
        {photos.length > 0 && (
          <div className="mt-6">
            <p className="text-xs uppercase tracking-[0.2em] text-xuma-muted mb-3">Galería</p>
            <div className="grid grid-cols-3 gap-2">
              {photos.map((p, i) => (
                <a key={i} href={p} target="_blank" rel="noreferrer" className="aspect-square rounded-xl overflow-hidden" data-testid={`gallery-${i}`}>
                  <img src={p} alt="" className="h-full w-full object-cover hover:scale-105 transition-transform" loading="lazy" />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Custom links */}
        {links.filter((l) => l.url).length > 0 && (
          <div className="mt-6 space-y-3">
            {links.filter((l) => l.url).map((l, i) => (
              <a key={i} href={l.url} onClick={() => track("link")} target="_blank" rel="noreferrer" data-testid={`custom-link-${i}`}
                className="flex items-center justify-between rounded-2xl bg-white border border-xuma-border px-5 h-14 hover:-translate-y-0.5 transition-transform">
                <span className="font-medium text-xuma-dark">{l.label || l.url}</span>
                <ArrowUpRight className="h-4 w-4 text-xuma-muted" />
              </a>
            ))}
          </div>
        )}

        {/* Contact form */}
        <div className="mt-8">
          <ContactForm slug={slug} primary={primary} name={card.name} />
        </div>

        {/* Socials */}
        {socials.length > 0 && (
          <div className="mt-8 flex justify-center gap-3 flex-wrap">
            {socials.map((s, i) => {
              const Icon = socialIcon(s.platform);
              return (
                <a key={i} href={s.url} target="_blank" rel="noreferrer" data-testid={`social-${i}`}
                  className="h-12 w-12 rounded-full grid place-items-center text-white hover:-translate-y-1 transition-transform"
                  style={{ background: primary }} aria-label={s.platform}>
                  <Icon className="h-5 w-5" />
                </a>
              );
            })}
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 text-center">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-xuma-muted hover:text-xuma-dark">
            Hecho con <span className="font-heading font-extrabold text-xuma-dark">XUMA</span>
          </Link>
        </div>
      </div>

      {card.assistant?.enabled && (
        <ChatWidget slug={slug} assistant={card.assistant} primary={primary} />
      )}
      <BookingDialog slug={slug} primary={primary} name={card.name} open={booking} onOpenChange={setBooking} />
    </div>
  );
}

const ActionRow = ({ href, icon: Icon, color, label, testid, external, onClick }) => (
  <a href={href} onClick={onClick} target={external ? "_blank" : undefined} rel={external ? "noreferrer" : undefined}
    data-testid={testid}
    className="flex items-center justify-between rounded-2xl bg-white border border-xuma-border px-4 h-16 hover:-translate-y-0.5 transition-transform">
    <span className="font-heading font-bold text-xuma-dark">{label}</span>
    <span className="h-11 w-11 rounded-full grid place-items-center text-white shrink-0" style={{ background: color }}>
      <Icon className="h-5 w-5" />
    </span>
  </a>
);
